'use client';

import { Fragment, useActionState, useEffect, useRef, useState } from 'react';
import { useFormStatus } from 'react-dom';
import { authenticate } from '@/lib/actions/auth.actions';
import { Button } from '@/components/ui/button';
import { AuthSocialButtons } from '@/components/auth/auth-social-buttons';
import { ModeToggle } from '@/components/mode-toggle';
import { LanguageSwitcher } from '@/components/layout/language-switcher';
import { AlertCircle, ArrowLeft, ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useT } from '@/lib/i18n/provider';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function SubmitButton({ is2FA }: { is2FA: boolean }) {
  const t = useT().login;
  const { pending } = useFormStatus();
  return (
    <Button
      type="submit"
      disabled={pending}
      className="mt-1.5 h-[50px] w-full rounded-[8px] text-[14px] font-bold uppercase tracking-[0.06em] transition-colors active:translate-y-px disabled:opacity-60"
    >
      {pending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          {is2FA ? t.verify : t.signIn}
          <ArrowRight className="ml-2 h-4 w-4" />
        </>
      )}
    </Button>
  );
}

export default function LoginPage() {
  const t = useT().login;
  const [state, dispatch] = useActionState(authenticate, undefined);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  /**
   * 🔴 O campo do código é CONTROLADO desde 28/08/2026 — e isso é correção de defeito, não estilo.
   *
   * Ele não tinha `value` e mutava `e.target.value` no `onChange`. Como os dois passos são ramos de
   * um mesmo ternário, o React alinhava `<div>`↔`<div>`, `<label>`↔`<label>` e `<input>`↔`<input>`
   * por índice e **reusava o nó DOM**: a transição virava controlado→não-controlado (o aviso do
   * Turbopack apontando o input de E-MAIL) e o campo do código nascia com o **e-mail dentro**.
   * A `key` distinta em cada ramo (abaixo) impede o reuso; o `value` daqui garante o valor certo
   * ainda que algum dia o reuso volte. Travado por `src/lib/__tests__/input-reuso-em-ramo.test.ts`.
   */
  const [code, setCode] = useState('');
  /** Código de recuperação tem hífen: afrouxa o tracking para não ficar ilegível. */
  const [codeLargo, setCodeLargo] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [fieldErr, setFieldErr] = useState<{ email?: boolean; password?: boolean; code?: boolean }>({});
  const cardRef = useRef<HTMLDivElement>(null);

  const shake = () => {
    const el = cardRef.current;
    if (!el) return;
    el.classList.remove('mp-shake');
    void el.offsetWidth; // reinicia a animação
    el.classList.add('mp-shake');
  };

  useEffect(() => {
    if (state?.message === '2FA_REQUIRED') {
      setShow2FA(true);
      // Zera o erro do passo anterior: sem isto, uma senha errada antes do desafio deixava
      // `fieldErr.email` ligado e o campo do código nascia pintado de vermelho sem motivo.
      setFieldErr({});
      return;
    }
    if (state?.success) {
      if (state.role === 'ADMIN') window.location.href = '/admin/super/stores';
      else if (state.isPlatformStaff) window.location.href = '/admin/super';
      else window.location.href = '/dashboard/welcome';
      return;
    }
    // Erro do servidor (credenciais/rate-limit/código) → shake + destaca campos
    if (state?.message && !state.success) {
      // Cada passo marca o SEU campo. Antes, o passo do código não marcava nada (e a caixa lia
      // `fieldErr.email`, de outro passo).
      setFieldErr(show2FA ? { code: true } : { email: true, password: true });
      shake();
      const el = cardRef.current?.querySelector<HTMLInputElement>(show2FA ? '#code' : '#email');
      el?.focus();
    }
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validação client-side antes de acionar a server action (só no modo email/senha).
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (show2FA) return; // 2FA: deixa a action rodar
    const emailBad = !EMAIL_RE.test(email.trim());
    const pwBad = password.length === 0;
    if (emailBad || pwBad) {
      e.preventDefault();
      setFieldErr({ email: emailBad, password: pwBad });
      shake();
      cardRef.current?.querySelector<HTMLInputElement>(emailBad ? '#email' : '#password')?.focus();
    }
  };

  const wrapCls =
    'flex h-12 items-center gap-2.5 rounded-[8px] border border-border bg-input px-3.5 transition-colors hover:border-accent/40 focus-within:border-accent focus-within:bg-card focus-within:ring-2 focus-within:ring-accent/25 data-[error=true]:border-destructive data-[error=true]:bg-[var(--mp-neg-bg)]';
  const inputCls =
    'flex-1 min-w-0 bg-transparent outline-none text-[16px] sm:text-[15px] text-foreground placeholder:text-muted-foreground/60';
  const labelCls = 'text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-[398px]">
        {/* Brand head (acima do card) */}
        <div className="relative mb-[22px] text-center">
          {/* Idioma ANTES do login: o screencast do App Review começa deslogado, e o
              header do dashboard (onde vive o outro seletor) ainda não existe aqui. */}
          <div className="absolute right-0 top-1 flex items-center gap-2">
            <LanguageSwitcher variant="outline" />
            <ModeToggle />
          </div>
          <img
            src="/favicon_io/android-chrome-512x512.png"
            alt="Mulply"
            width={60}
            height={60}
            className="mx-auto mb-[13px] h-[60px] w-[60px] rounded-[16px] shadow-sm"
          />
          <div className="text-[26px] font-extrabold leading-none tracking-[-0.5px] text-foreground">Mulply</div>
        </div>

        {/* Card */}
        <div
          ref={cardRef}
          className="rounded-[16px] border border-border bg-card p-6 shadow-[0_8px_32px_rgba(0,0,0,0.12)] sm:p-8"
        >
          <h1 className="text-center text-[22px] font-extrabold leading-tight tracking-[-0.4px] text-foreground">
            {show2FA ? t.twoFactorTitle : t.title}
          </h1>
          <p className="mb-[22px] mt-1.5 text-center text-[14px] text-muted-foreground">
            {show2FA ? t.twoFactorSubtitle : t.subtitle}
          </p>

          <form action={dispatch} onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/*
              🔴 `key` distinta em cada ramo — sem ela o React reconcilia os filhos por ÍNDICE,
              casa `<div>`↔`<div>` e `<input>`↔`<input>`, e REUSA o nó DOM entre os dois passos.
              Foi assim que o campo do código apareceu com o e-mail dentro. `Fragment` e não `<div>`
              de propósito: o `<form>` é `flex flex-col gap-4` e um wrapper mudaria o espaçamento.
              Efeito colateral desejado: com montagem de verdade, o `autoFocus` do campo volta a
              funcionar — ele só dispara no mount, e o nó reusado nunca era montado.
            */}
            {!show2FA ? (
              <Fragment key="credenciais">
                <AuthSocialButtons />

                {/* Divisor */}
                <div className="flex items-center gap-3 py-1">
                  <span className="h-px flex-1 bg-border" />
                  <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.11em] text-muted-foreground/60">
                    {t.divider}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className={labelCls}>{t.email}</label>
                  <div data-error={fieldErr.email ? 'true' : undefined} className={wrapCls}>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className={inputCls}
                      placeholder={t.emailPlaceholder}
                      autoComplete="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (fieldErr.email) setFieldErr((f) => ({ ...f, email: false })); }}
                    />
                  </div>
                </div>

                {/* Senha */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className={labelCls}>{t.password}</label>
                    <Link href="/forgot-password" className="text-[11px] font-bold uppercase tracking-[0.06em] text-accent hover:underline">
                      {t.forgotPassword}
                    </Link>
                  </div>
                  <div data-error={fieldErr.password ? 'true' : undefined} className={wrapCls}>
                    <input
                      id="password"
                      name="password"
                      type={showPw ? 'text' : 'password'}
                      required
                      className={inputCls}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); if (fieldErr.password) setFieldErr((f) => ({ ...f, password: false })); }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? t.hidePassword : t.showPassword}
                      className="flex items-center rounded-[5px] p-0.5 text-muted-foreground/70 transition-colors hover:text-accent"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </Fragment>
            ) : (
              <Fragment key="desafio-2fa">
                {/* 2FA: reenvia credenciais + código */}
                <input type="hidden" name="email" value={email} />
                <input type="hidden" name="password" value={password} />
                <div className="flex flex-col gap-2">
                  <label htmlFor="code" className={`${labelCls} text-center`}>{t.codeLabel}</label>
                  <div data-error={fieldErr.code ? 'true' : undefined} className={`${wrapCls} h-16 justify-center`}>
                    {/*
                      🔴 Aceita TOTP (6 dígitos) OU código de recuperação
                      (`XXXX-XXXX-XXXX-XXXX`, 19 chars; o formato legado de 9 continua valendo).
                      Até 28/08/2026 era `inputMode="numeric"` + `maxLength={6}`: o código de
                      recuperação simplesmente não cabia, e o ramo que o valida
                      (`auth.ts`, `verifyBackupCode`) era inalcançável pela tela. Quem perdesse
                      o autenticador ficava sem entrada.

                      ⚠️ A normalização de HÍFEN mora no SERVIDOR, dentro de `verifyBackupCode` —
                      não aqui. Importar `@/lib/backup-codes` num componente `'use client'`
                      arrastaria `node:crypto` para o bundle do browser. Aqui só se limpa o que é
                      visual (caixa e espaço); digitar sem hífen funciona porque o servidor
                      reagrupa.

                      O `tracking` largo é bonito no TOTP e ilegível no código com hífen — por
                      isso ele afrouxa quando o valor deixa de ser só dígito.
                    */}
                    <input
                      id="code"
                      name="code"
                      type="text"
                      autoComplete="one-time-code"
                      maxLength={19}
                      required
                      autoFocus
                      placeholder="000000"
                      value={code}
                      onChange={(e) => {
                        // Normaliza antes de enviar: o usuário cola de gerenciador de senhas com
                        // espaço, e digita minúscula. O servidor compara hash do texto exato.
                        const v = e.target.value.trim().toUpperCase().replace(/\s+/g, '');
                        setCode(v);
                        setCodeLargo(/[^0-9]/.test(v));
                        if (fieldErr.code) setFieldErr((f) => ({ ...f, code: false }));
                      }}
                      className={`w-full bg-transparent text-center font-black text-foreground outline-none placeholder:text-muted-foreground/40 ${codeLargo ? 'text-2xl tracking-[0.15em]' : 'text-3xl tracking-[0.5em]'}`}
                      style={{ fontFamily: 'var(--font-jetbrains-mono, "JetBrains Mono", monospace)' }}
                    />
                  </div>
                  <p className="text-center text-[11.5px] text-muted-foreground">
                    {t.lostDevice}
                  </p>
                </div>
              </Fragment>
            )}

            {/* Mensagem de erro do servidor */}
            {state?.message && state.message !== '2FA_REQUIRED' && !state.success && (
              <div className="flex items-start gap-2.5 rounded-[8px] border border-destructive/20 bg-destructive/10 p-3 text-[13px] font-medium text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{state.message}</span>
              </div>
            )}

            {/* Sucesso (interino, antes do redirect) */}
            {state?.success && (
              <div className="rounded-[8px] border border-[var(--mp-pos-border)] bg-[var(--mp-pos-bg)] p-3 text-center text-[12px] font-bold uppercase tracking-[0.06em] text-[var(--mp-pos-text)]">
                {t.accessGranted}
              </div>
            )}

            <SubmitButton is2FA={show2FA} />

            {show2FA && (
              <button
                type="button"
                onClick={() => { setShow2FA(false); setFieldErr({}); setCode(''); setCodeLargo(false); }}
                className="mx-auto flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t.backToEmail}
              </button>
            )}
          </form>

          {!show2FA && (
            <div className="mt-[22px] border-t border-border pt-5 text-center text-[13px] text-muted-foreground">
              {t.firstTime}{' '}
              <Link href="/register" className="font-semibold text-accent hover:underline">{t.createAccount}</Link>
            </div>
          )}
        </div>

        {/* Rodapé da página */}
        <div className="mt-6 flex items-center justify-center gap-3.5 text-[11px] font-semibold uppercase tracking-[0.06em]">
          <Link href="/terms" className="text-muted-foreground/60 transition-colors hover:text-accent">{t.terms}</Link>
          <span className="h-[3px] w-[3px] rounded-full bg-muted-foreground/40" />
          <Link href="/privacy" className="text-muted-foreground/60 transition-colors hover:text-accent">{t.privacy}</Link>
          <span className="h-[3px] w-[3px] rounded-full bg-muted-foreground/40" />
          <a href="mailto:suporte@mulply.com" className="text-muted-foreground/60 transition-colors hover:text-accent">{t.support}</a>
        </div>
      </div>
    </div>
  );
}
